import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingCapeTownPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Cape Town Professional Painters - Residential & Commercial Painting | Roof Coating Specialists',
    description: 'Leading painting services across Cape Town CBD, Southern Suburbs, and Northern Suburbs. Expert residential, commercial, and roof painting with over 18 years of experience. Quality workmanship guaranteed.',
    keywords: 'painters Cape Town, house painters Cape Town, residential painting Cape Town, commercial painting, roof painting, interior painting, exterior painting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Cape Town',
          description: 'Professional painting services across Cape Town',
          address: 'Cape Town, Western Cape',
          priceRange: '$$',
          serviceArea: ['Cape Town CBD', 'Southern Suburbs', 'Northern Suburbs', 'Atlantic Seaboard', 'City Bowl']
        }),
        generateServiceSchema({
          name: 'Complete Painting Solutions',
          description: 'Residential, commercial, and roof painting services',
          provider: 'Painters Cape Town',
          areaServed: ['Cape Town CBD', 'Southern Suburbs', 'Northern Suburbs'],
          serviceType: ['Residential Painting', 'Commercial Painting', 'Roof Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Painters Cape Town",
    location: "Cape Town, Western Cape",
    website: "https://painterscapetown.com/",
    heroImage: "https://public.readdy.ai/ai/img_res/257bb0a5a5eb3744be5c41223045436d.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20modern%20cape%20town%20home%20exterior%20with%20mountain%20views%20contemporary%20architecture%20professional%20finish%20clear%20blue%20sky&width=800&height=600&seq=gallery-ct1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=commercial%20building%20painting%20project%20in%20cape%20town%20cbd%20modern%20office%20building%20professional%20painters%20working%20high%20rise%20structure&width=800&height=600&seq=gallery-ct2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=elegant%20interior%20room%20painting%20cape%20town%20style%20sophisticated%20color%20palette%20modern%20furniture%20natural%20lighting%20professional%20finish&width=800&height=600&seq=gallery-ct3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=roof%20painting%20project%20on%20cape%20town%20property%20with%20city%20views%20professional%20application%20weather%20resistant%20coating%20modern%20residential%20building&width=800&height=600&seq=gallery-ct4&orientation=landscape"
    ],
    services: [
      {
        title: "Residential Painting",
        description: "Comprehensive residential painting services for homes throughout Cape Town. From apartments to luxury estates, we deliver exceptional results that enhance your property's value and appeal.",
        features: ["Interior painting all rooms", "Exterior facade painting", "Heritage home restoration", "Modern apartment painting", "Color design consultation", "Premium paint selection"]
      },
      {
        title: "Commercial Painting",
        description: "Professional commercial painting services for businesses across Cape Town. We work efficiently to minimize disruption while delivering outstanding results for offices, retail spaces, and industrial facilities.",
        features: ["Office buildings & complexes", "Retail stores & shopping centers", "Industrial facilities", "Strata & body corporate", "High-rise buildings", "After-hours scheduling"]
      },
      {
        title: "Roof Painting",
        description: "Specialized roof painting and waterproofing services to protect your Cape Town property from the elements. We use advanced coatings designed for the local climate.",
        features: ["Tile roof painting", "Metal roof coating", "Waterproofing treatments", "Heat-reflective coatings", "Leak repair & prevention", "Roof cleaning & preparation"]
      }
    ],
    serviceAreas: ["Cape Town CBD", "Southern Suburbs", "Northern Suburbs", "Atlantic Seaboard", "City Bowl", "Constantia", "Claremont", "Rondebosch", "Newlands", "Table View", "Camps Bay", "Sea Point"],
    about: "Painters Cape Town is the leading painting company serving the greater Cape Town metropolitan area. With over 18 years of experience and a team of highly skilled professionals, we have completed thousands of successful painting projects across residential, commercial, and industrial sectors throughout the Mother City.",
    aboutExtended: [
      "Our company was founded on the principles of quality workmanship, reliability, and customer satisfaction. We understand that Cape Town's unique climate and diverse architectural styles require specialized knowledge and expertise. From historic Victorian homes in the Southern Suburbs to modern apartments in the CBD, from beachfront properties on the Atlantic Seaboard to commercial buildings in the business districts, we have the experience and skills to handle any painting project.",
      "We pride ourselves on using only premium quality paints and materials from leading international and local brands. Our team stays current with the latest painting techniques, technologies, and industry best practices through ongoing training and professional development. We are fully licensed, insured, and compliant with all relevant safety and environmental regulations.",
      "What truly sets us apart is our commitment to customer service and project management excellence. From your initial consultation through to final completion, we maintain clear communication, adhere to agreed timelines, and ensure your complete satisfaction. Our comprehensive approach includes detailed surface preparation, expert color consultation, professional application, and thorough quality control at every stage."
    ],
    ourApproach: [
      "Detailed on-site consultation and assessment",
      "Comprehensive project planning and scheduling",
      "Thorough surface preparation and repairs",
      "Premium quality paints and materials",
      "Professional application techniques",
      "Strict quality control procedures",
      "Clean and organized work practices",
      "Final inspection and client walkthrough",
      "Comprehensive warranty coverage"
    ],
    whyChoose: [
      "18+ years serving Cape Town",
      "Thousands of completed projects",
      "Fully licensed and insured",
      "Experienced professional team",
      "Premium quality materials",
      "Competitive pricing",
      "Free detailed quotes",
      "Comprehensive warranties",
      "Excellent customer reviews",
      "All areas of Cape Town covered",
      "Residential & commercial expertise",
      "Heritage restoration specialists"
    ],
    testimonials: [
      {
        name: "Robert Mitchell",
        rating: 5,
        text: "Exceptional service from start to finish! The team was professional, efficient, and the quality of work is outstanding. Our office building looks fantastic. Highly recommend Painters Cape Town!"
      },
      {
        name: "Jennifer Adams",
        rating: 5,
        text: "We've used them for multiple properties and they never disappoint. Great attention to detail, reliable, and excellent value. They're our go-to painters in Cape Town."
      },
      {
        name: "Mohammed Hassan",
        rating: 5,
        text: "Very impressed with their professionalism and expertise. They completed our home renovation painting on time and within budget. The results are beautiful!"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Painters Cape Town</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Painters Cape Town</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We provide professional painting services throughout the greater Cape Town metropolitan area:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">Comprehensive painting solutions for residential and commercial properties throughout Cape Town.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">All Cape Town suburbs</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 7:30AM-5:30PM</p>
                    <p className="text-gray-600">Sat: 8AM-2PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Table View</li>
                <li>Melkbosstrand</li>
                <li>Durbanville</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
